import { useEffect, useState } from "react";

/**
 * Keep `--app-height` on the height of the visual viewport.
 *
 * A soft keyboard does not shrink the layout viewport, it covers it -- and the browser then
 * scrolls the document to bring the caret above it, which on a full-height app slides the chat
 * header off the top while you type. Sizing the app to the visible part instead leaves the header
 * where it is and takes the room out of the transcript, which can scroll.
 *
 * `interactive-widget=resizes-content` in the viewport meta asks the browser to do this natively;
 * only Chrome honours it so far, and the two agree where it works.
 */
export function trackViewportHeight(): void {
	const vv = window.visualViewport;
	// A soft keyboard is the only thing this is for, and pinch-zoom also moves the visual viewport
	// -- on a mouse, 100dvh is already the right answer.
	if (!vv || !window.matchMedia?.("(pointer: coarse)").matches) return;
	const apply = () => {
		document.documentElement.style.setProperty("--app-height", `${vv.height}px`);
		// Undo the scroll the browser did to reveal the caret: once the app fits the visible area
		// there is nothing left for it to reveal.
		if (window.scrollY !== 0) window.scrollTo(0, 0);
	};
	vv.addEventListener("resize", apply);
	vv.addEventListener("scroll", apply);
	apply();
}

/** Pairs with the `@media (max-width: 760px)` block in styles.css -- change both together. */
const NARROW = "(max-width: 760px)";

/**
 * True while the layout is the phone one: the sidebar is a drawer, and the chat header rather
 * than the composer carries the model button. Keyed on the same width the stylesheet uses, so
 * a window dragged across the breakpoint swaps both together.
 */
export function useIsNarrow(): boolean {
	const [narrow, setNarrow] = useState(() => typeof matchMedia === "function" && matchMedia(NARROW).matches);
	useEffect(() => {
		if (typeof matchMedia !== "function") return;
		const mq = matchMedia(NARROW);
		const update = () => setNarrow(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);
	return narrow;
}
